import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import useApi from '../api';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Plus, RefreshCw, Rocket } from 'lucide-react';
import {
    Job,
    JobStatusBadges,
    Service,
    apiErrorMessage,
    formatCredits,
    formatRelativeTime,
    jobSubject,
    personLabel,
} from '../services/servicesShared';
import AssignJobDialog from './AssignJobDialog';

type StatusFilter = 'all' | 'on' | 'off' | 'blocked';

/**
 * Jobs list (GET /jobs?service_id&status&blocked). Org-wide on the Jobs
 * page; filtered to one service inside the service editor.
 */
export const JobsList: React.FC<{
    active?: boolean;
    /** Lock the list to one service (editor Jobs tab). */
    service?: Service | null;
    /** Offered in the service filter and the Assign dialog on the org-wide list. */
    services?: Service[];
    /** Called after a service-level change (go-live) so the host can reload the service's counts. */
    onServiceChanged?: () => void;
}> = ({ active = true, service, services = [], onServiceChanged }) => {
    const navigate = useNavigate();
    const { get, post } = useApi();
    const [jobs, setJobs] = useState<Job[]>([]);
    const [loading, setLoading] = useState(true);
    const [serviceFilter, setServiceFilter] = useState<string>('all');
    const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
    const [assignOpen, setAssignOpen] = useState(false);
    const [goLiveOpen, setGoLiveOpen] = useState(false);
    const [goingLive, setGoingLive] = useState(false);
    const shadowCount = service?.jobs_shadow ?? 0;

    const goLiveAll = async () => {
        if (!service) return;
        setGoingLive(true);
        try {
            const response = await post(`/services/${service.id}/jobs/go-live`, { set_default: true });
            const restarted: number = response.data?.restarted ?? 0;
            const skipped: number = response.data?.skipped?.length ?? 0;
            toast.success(
                `${restarted} job${restarted === 1 ? '' : 's'} went live with a clean slate${skipped ? `; ${skipped} skipped (mid-session — try again in a minute)` : ''}. New jobs start live.`,
            );
            setGoLiveOpen(false);
            onServiceChanged?.();
            void fetchJobs();
        } catch (error) {
            toast.error(apiErrorMessage(error, 'Could not take the service live'));
        } finally {
            setGoingLive(false);
        }
    };

    const serviceId = service?.id || (serviceFilter !== 'all' ? serviceFilter : null);

    const fetchJobs = useCallback(async () => {
        try {
            setLoading(true);
            const params = new URLSearchParams();
            if (serviceId) params.set('service_id', serviceId);
            if (statusFilter === 'on' || statusFilter === 'off') params.set('status', statusFilter);
            if (statusFilter === 'blocked') params.set('blocked', 'true');
            const query = params.toString();
            const response = await get(`/jobs${query ? `?${query}` : ''}`);
            setJobs(response.data?.data || []);
        } catch (error) {
            console.error('Error fetching jobs:', error);
        } finally {
            setLoading(false);
        }
    }, [get, serviceId, statusFilter]);

    useEffect(() => {
        if (active) void fetchJobs();
    }, [active, fetchJobs]);

    const assignable = useMemo(
        () => (service ? [service] : services).filter((item) => item.status === 'active' && item.published_version_number != null),
        [service, services],
    );
    const showServiceColumn = !service;
    const columnCount = showServiceColumn ? 7 : 6;

    return (
        <div>
            <div className="flex flex-wrap items-center gap-2 mb-3">
                {!service && services.length > 0 && (
                    <Select value={serviceFilter} onValueChange={setServiceFilter}>
                        <SelectTrigger className="w-[220px]">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All services</SelectItem>
                            {services.map((item) => (
                                <SelectItem key={item.id} value={item.id}>
                                    {item.name}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                )}
                <Select value={statusFilter} onValueChange={(value) => setStatusFilter(value as StatusFilter)}>
                    <SelectTrigger className="w-[170px]">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">Any status</SelectItem>
                        <SelectItem value="on">On</SelectItem>
                        <SelectItem value="blocked">On · waiting</SelectItem>
                        <SelectItem value="off">Off</SelectItem>
                    </SelectContent>
                </Select>
                <Button variant="ghost" size="icon" title="Refresh" onClick={() => void fetchJobs()} disabled={loading}>
                    <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                </Button>
                <div className="grow" />
                {service && shadowCount > 0 && (
                    <Button variant="outline" onClick={() => setGoLiveOpen(true)} title="Every rehearsing job restarts live and new jobs start live">
                        <Rocket className="h-4 w-4 mr-1" /> Go live ({shadowCount} rehearsing)
                    </Button>
                )}
                <Button onClick={() => setAssignOpen(true)} disabled={!assignable.length} title={assignable.length ? undefined : 'Publish a service first'}>
                    <Plus className="h-4 w-4 mr-1" /> Assign job
                </Button>
            </div>

            {service && (
                <Dialog open={goLiveOpen} onOpenChange={setGoLiveOpen}>
                    <DialogContent className="max-w-md">
                        <DialogHeader>
                            <DialogTitle>Take {service.name} live?</DialogTitle>
                            <DialogDescription>
                                {shadowCount} job{shadowCount === 1 ? '' : 's'} in shadow mode start over for real: writes happen and messages reach
                                the customer. Each rehearsal&rsquo;s state, memory and workspace is set aside (its &ldquo;would have&rdquo; entries stay in
                                the job&rsquo;s log for review) and a setup wake is queued per job. New jobs for this service will start live.
                            </DialogDescription>
                        </DialogHeader>
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setGoLiveOpen(false)} disabled={goingLive}>
                                Cancel
                            </Button>
                            <Button onClick={goLiveAll} disabled={goingLive}>
                                {goingLive ? 'Going live…' : 'Go live'}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            )}

            <div className="border rounded-lg shadow overflow-hidden">
                <Table className="table-fixed w-full">
                    <TableHeader>
                        <TableRow>
                            <TableHead style={{ width: 220 }}>Customer</TableHead>
                            {showServiceColumn && <TableHead style={{ width: 180 }}>Service</TableHead>}
                            <TableHead>Headline</TableHead>
                            <TableHead style={{ width: 200 }}>Status</TableHead>
                            <TableHead style={{ width: 120 }}>Last wake</TableHead>
                            <TableHead style={{ width: 110 }}>Next wake</TableHead>
                            <TableHead style={{ width: 90 }} className="text-right">
                                Credits
                            </TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {loading ? (
                            [1, 2, 3].map((i) => (
                                <TableRow key={i} className="animate-pulse">
                                    {[...Array(columnCount)].map((_, j) => (
                                        <TableCell key={j} className="px-6 py-4">
                                            <div className="h-4 bg-muted rounded w-3/4" />
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))
                        ) : jobs.length ? (
                            jobs.map((job) => (
                                <TableRow key={job.id} className="text-md cursor-pointer" onClick={() => navigate(`/jobs/${job.id}`)}>
                                    <TableCell className="overflow-hidden">
                                        <div className="text-sm font-medium truncate" title={jobSubject(job)}>
                                            {jobSubject(job)}
                                        </div>
                                        {job.tenant && (
                                            <div className="text-xs text-muted-foreground truncate">Customer: {personLabel(job.owner)}</div>
                                        )}
                                    </TableCell>
                                    {showServiceColumn && (
                                        <TableCell className="overflow-hidden">
                                            <span className="text-sm truncate block" title={job.service_name || undefined}>
                                                {job.service_name || '—'}
                                            </span>
                                        </TableCell>
                                    )}
                                    <TableCell className="overflow-hidden">
                                        <span className="text-sm truncate block" title={job.headline || undefined}>
                                            {job.headline || <span className="text-muted-foreground italic">No headline yet</span>}
                                        </span>
                                        {job.last_wake_summary && (
                                            <span className="text-xs text-muted-foreground truncate block">{job.last_wake_summary}</span>
                                        )}
                                    </TableCell>
                                    <TableCell className="overflow-hidden">
                                        <JobStatusBadges job={job} />
                                    </TableCell>
                                    <TableCell className="text-muted-foreground whitespace-nowrap">{formatRelativeTime(job.last_wake_at)}</TableCell>
                                    <TableCell className="text-muted-foreground whitespace-nowrap">
                                        {job.status === 'on' ? formatRelativeTime(job.next_wake_at) : '—'}
                                    </TableCell>
                                    <TableCell className="text-right tabular-nums">{formatCredits(job.credits_used_period)}</TableCell>
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={columnCount} className="h-24 text-center text-sm text-muted-foreground">
                                    {service
                                        ? 'No jobs for this service yet. Assign one to a customer to start.'
                                        : 'No jobs yet. Publish a service and assign it to a customer, or let customers subscribe.'}
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </div>

            <AssignJobDialog
                open={assignOpen}
                onClose={() => setAssignOpen(false)}
                service={service || null}
                services={assignable}
                onAssigned={(job) => navigate(`/jobs/${job.id}`)}
                onBulkAssigned={() => void fetchJobs()}
            />
        </div>
    );
};

export default JobsList;
